/**
 * CargasPage — Listado de cargas de combustible + botón "Nueva carga" con OCR.
 * Piloto ve solo sus embarcaciones asignadas; operaciones/admin ven todas.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TextField, MenuItem, Chip, Button, IconButton, Tooltip,
  CircularProgress, Snackbar, Alert,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaidIcon from '@mui/icons-material/Paid';
import NuevaCargaDialog from './NuevaCargaDialog';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  // Superadmin puede cambiar de empresa desde el sidebar (Layout escribe
  // `empresa_activa` en localStorage). Respetar ese valor por encima de
  // `user.company_id` para no quedar fuera de sync con el selector activo.
  const activa = localStorage.getItem('empresa_activa');
  if (activa) return activa;
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}
function getUserRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CO', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const ESTADO_PAGO = {
  a_credito: { label: 'A crédito', bg: '#fef3c7', fg: '#92400e' },
  pagada: { label: 'Pagada', bg: '#dcfce7', fg: '#166534' },
};

export default function CargasPage() {
  const empresaId = getEmpresaId();
  const role = getUserRole();
  const puedeMarcarPagada = ['operaciones', 'admin', 'superadmin'].includes(role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totales, setTotales] = useState({ galones: 0, pagado: 0 });
  const [filtros, setFiltros] = useState({
    embarcacion_id: '', estacion_id: '', estado_pago: '',
    fecha_desde: '', fecha_hasta: '',
  });
  const [embarcaciones, setEmbarcaciones] = useState([]);
  const [estaciones, setEstaciones] = useState([]);
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId, limit: '200' });
      Object.entries(filtros).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await fetch(`${API_BASE}/turismo/combustible/cargas?${params}`,
        { headers: getAuthHeaders() });
      if (r.ok) {
        const d = await r.json();
        setRows(d.items || []);
        setTotales({ galones: d.total_galones || 0, pagado: d.total_pagado || 0 });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [empresaId, filtros]);

  const cargarCatalogos = useCallback(async () => {
    try {
      const [rE, rS] = await Promise.all([
        fetch(`${API_BASE}/turismo/combustible/mis-embarcaciones?empresa_id=${empresaId}`,
          { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/combustible/estaciones?empresa_id=${empresaId}`,
          { headers: getAuthHeaders() }),
      ]);
      if (rE.ok) setEmbarcaciones(await rE.json());
      if (rS.ok) setEstaciones(await rS.json());
    } catch (e) { console.error(e); }
  }, [empresaId]);

  useEffect(() => { cargarCatalogos(); }, [cargarCatalogos]);
  useEffect(() => { cargar(); }, [cargar]);

  const setF = (k) => (e) => setFiltros(p => ({ ...p, [k]: e.target.value }));

  const marcarPagada = async (id) => {
    try {
      const r = await fetch(
        `${API_BASE}/turismo/combustible/cargas/${id}/marcar-pagada?empresa_id=${empresaId}`,
        { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({}) }
      );
      if (r.ok) {
        setSnack({ open: true, msg: 'Carga marcada como pagada', severity: 'success' });
        cargar();
      }
    } catch (e) {
      setSnack({ open: true, msg: 'Error de red', severity: 'error' });
    }
  };

  return (
    <Box>
      {/* Barra superior con totales + botón nuevo */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 1.5, borderLeft: '4px solid #dc2626', minWidth: 140 }}>
            <Typography sx={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>
              Galones (filtro)
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
              {Number(totales.galones).toLocaleString('es-CO', { maximumFractionDigits: 2 })}
            </Typography>
          </Paper>
          <Paper sx={{ p: 1.5, borderLeft: '4px solid #16a34a', minWidth: 160 }}>
            <Typography sx={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>
              Total pagado
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
              {fmtMoney(totales.pagado)}
            </Typography>
          </Paper>
        </Box>
        <Tooltip title="Refrescar">
          <IconButton onClick={cargar}><RefreshIcon /></IconButton>
        </Tooltip>
        <Button variant="contained" startIcon={<AddCircleIcon />}
          onClick={() => setNuevaOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            fontWeight: 700, textTransform: 'none',
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.35)',
            '&:hover': { background: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)' },
          }}
        >
          Nueva carga
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <TextField select size="small" label="Embarcación"
          value={filtros.embarcacion_id} onChange={setF('embarcacion_id')}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {embarcaciones.map(e => (
            <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Estación"
          value={filtros.estacion_id} onChange={setF('estacion_id')}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {estaciones.map(e => (
            <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Estado pago"
          value={filtros.estado_pago} onChange={setF('estado_pago')}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="a_credito">A crédito</MenuItem>
          <MenuItem value="pagada">Pagada</MenuItem>
        </TextField>
        <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
          value={filtros.fecha_desde} onChange={setF('fecha_desde')} sx={{ width: 150 }} />
        <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
          value={filtros.fecha_hasta} onChange={setF('fecha_hasta')} sx={{ width: 150 }} />
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Embarcación</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Estación</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Galones</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">$/gal</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Piloto</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Factura</TableCell>
              {puedeMarcarPagada && <TableCell sx={{ fontWeight: 700 }} align="center">Acción</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={puedeMarcarPagada ? 10 : 9} align="center" sx={{ py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#dc2626' }} />
              </TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={puedeMarcarPagada ? 10 : 9} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                Sin cargas registradas. Toca "Nueva carga" para empezar.
              </TableCell></TableRow>
            )}
            {!loading && rows.map(c => {
              const est = ESTADO_PAGO[c.estado_pago] || ESTADO_PAGO.a_credito;
              return (
                <TableRow key={c.id} hover
                  sx={{ opacity: c.revertida ? 0.4 : 1 }}>
                  <TableCell sx={{ fontSize: 12 }}>{fmtDate(c.fecha_carga)}</TableCell>
                  <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{c.embarcacion_nombre || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{c.estacion_nombre || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {Number(c.galones).toLocaleString('es-CO', { maximumFractionDigits: 3 })}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: '#64748b' }}>
                    {fmtMoney(c.precio_por_galon)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700 }}>
                    {fmtMoney(c.total_pagado)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, color: '#64748b' }}>
                    {c.cargado_por_nombre || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip label={est.label} size="small"
                      sx={{ bgcolor: est.bg, color: est.fg, fontSize: 10.5, height: 22, fontWeight: 700 }}
                    />
                    {c.revertida && (
                      <Chip label="Reversada" size="small"
                        sx={{ ml: 0.5, bgcolor: '#fee2e2', color: '#991b1b', fontSize: 10, height: 20 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {c.factura_url && (
                      <IconButton size="small"
                        onClick={() => window.open(c.factura_url, '_blank')}>
                        <ReceiptLongIcon fontSize="small" sx={{ color: '#dc2626' }} />
                      </IconButton>
                    )}
                  </TableCell>
                  {puedeMarcarPagada && (
                    <TableCell align="center">
                      {c.estado_pago === 'a_credito' && !c.revertida && (
                        <Tooltip title="Marcar como pagada a la estación">
                          <IconButton size="small" onClick={() => marcarPagada(c.id)}>
                            <PaidIcon fontSize="small" sx={{ color: '#16a34a' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <NuevaCargaDialog
        open={nuevaOpen}
        embarcaciones={embarcaciones}
        estaciones={estaciones}
        empresaId={empresaId}
        onClose={() => setNuevaOpen(false)}
        onSuccess={(msg) => {
          setNuevaOpen(false);
          setSnack({ open: true, msg, severity: 'success' });
          cargar();
        }}
        onError={(msg) => setSnack({ open: true, msg, severity: 'error' })}
      />

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
